/**
 * @file src/core/i18n.js
 * ctx.t() — dịch chuỗi. Bắt buộc dùng cho MỌI chuỗi học sinh nhìn thấy.
 * ESLint rule no-hardcoded-vietnamese chặn chuỗi tiếng Việt trong src/lessons/**.
 */

/**
 * @typedef {(key: string, vars?: Record<string, string | number>) => string} TranslateFn
 */

/**
 * @typedef {Object} I18n
 * @property {TranslateFn} t
 * @property {() => string} lang
 * @property {(lang: string, dict: Record<string, string>) => void} setDict
 * @property {(lang: string) => boolean} has
 * @property {() => string[]} languages
 * @property {() => string[]} missingKeys   Debug: key đã gọi mà không có trong dict
 */

/**
 * @param {{ lang: string, dicts: Record<string, Record<string, string>> }} opts
 * @returns {I18n}
 */
export function createI18n(opts) {
  let current = opts.lang;
  /** @type {Record<string, Record<string, string>>} */
  const dicts = { ...opts.dicts };
  /** @type {Set<string>} */
  const missing = new Set();

  /** @type {TranslateFn} */
  const t = (key, vars) => {
    const dict = dicts[current] ?? {};
    let s = dict[key];

    if (s === undefined) {
      // fallback sang tiếng Việt rồi mới tới chính key
      s = dicts.vi?.[key];
      if (s === undefined) {
        missing.add(`${current}:${key}`);
        if (import.meta.env?.DEV) console.warn(`[i18n] thiếu key "${key}" (${current})`);
        return key;
      }
    }

    if (vars) {
      s = s.replace(/\{(\w+)\}/g, (m, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : m,
      );
    }
    return s;
  };

  return {
    t,
    lang: () => current,
    setDict(lang, dict) {
      dicts[lang] = dict;
      current = lang;
    },
    has(lang) {
      return Boolean(dicts[lang]);
    },
    languages() {
      return Object.keys(dicts);
    },
    missingKeys() {
      return [...missing];
    },
  };
}
